import {
  type IFlagsmithFeature,
  useFlags,
  useFlagsmith,
} from '@flagsmith/flagsmith/react';
import { useEffect, useMemo } from 'react';
import type { ActiveOrganizationProps } from '../providers/userContextProvider';

export enum FlagsmithFlags {
  workflow_review = 'workflow_review',
  auto_invite = 'auto_invite',
  multi_deposit_review = 'multi_deposit_review',
  repcode_access = 'repcode_access',
  configurable_table_pages = 'configurable_table_pages',
  mackenzie_account_page = 'mackenzie_account_page',
  conditional_account_page_configuration = 'conditional_account_page_configuration',
  acb_rework = 'acb_rework',
  spanish_language_support = 'spanish_language_support',
  weekdays_only_calendar = 'weekdays_only_calendar',
  text_variants = 'text_variants',
  form_mapper = 'form_mapper',
  show_account_type = 'show_account_type',
  portfolio_trading_prototype = 'portfolio_trading_prototype',
  widgets_additional_info = 'widgets_additional_info',
  close_sub_account = 'close_sub_account',
  alert_banners = 'alert_banners',
  asset_class_portfolios = 'asset_class_portfolios',
  audit_log_widget_v2 = 'audit_log_widget_v2',
  bulk_trader_revamp = 'bulk_trader_revamp',
  docusign_in_app_signing = 'docusign_in_app_signing',
  recon_flags_dashboard = 'recon_flags_dashboard',
  recon_activities_dashboard = 'recon_activities_dashboard',
  household_portfolio = 'household_portfolio',
  household_portfolio_allocation_methodology = 'household_portfolio_allocation_methodology',
  household_model_portfolio_widget = 'household_model_portfolio_widget',
  forms_idempotent_form_steps = 'forms_idempotent_form_steps',
  use_fee_enable_toggle = 'use_fee_enable_toggle',
  home_page_product_aum_exposure = 'home_page_product_aum_exposure',
  use_multi_billing_cycle_export = 'use_multi_billing_cycle_export',
  tcp_spouse_lookup = 'tcp_spouse_lookup',
  fee_data_migration = 'fee_data_migration',
  use_fee_tier_page = 'use_fee_tier_page',
  use_billing_schedule_page = 'use_billing_schedule_page',
  workflow_household_branch_conditions = 'workflow_household_branch_conditions',
  resp_transferred_from_another_institution = 'resp_transferred_from_another_institution',
}

export const useFlagsmithFlags = (organization: ActiveOrganizationProps) => {
  const flagsmithFlags = useFlags(Object.values(FlagsmithFlags));
  const flagsmith = useFlagsmith();
  // Use publicParents instead of parents to get the full inheritance chain
  // publicParents provides public info about parent organizations going up
  // the full inheritance chain to the top level, regardless of user permissions
  const organizationParentsTrait = (organization.publicParents || [])
    .map((p) => p.id)
    .join('/');
  useEffect(() => {
    if (!organization.id) return;
    void flagsmith.identify(`org:${organization.id}`, {
      name: organization.translatedName?.en || null,
      parents: organizationParentsTrait,
    });
  }, [
    organization.id,
    organizationParentsTrait,
    organization.translatedName?.en,
    flagsmith,
  ]);

  return useMemo(() => {
    const enabledFlags = Object.entries(flagsmithFlags)
      .filter(([, state]) => (state as IFlagsmithFeature).enabled)
      .map(([key]) => key);

    return new Set(enabledFlags as FlagsmithFlags[]);
  }, [organization, flagsmithFlags]);
};
